import { Fragment, useEffect, useState } from "react"
import { Box, chakra, Icon, Text, VStack } from "@chakra-ui/react"
import { AssetStatus, TenantStatusCopyType } from "@hydrologiq/gql-experience/graphql"
import PaginatedFigureCard from "@hydrologiq/shared/figures/generic/PaginatedFigureCard"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"
import { useCopyStatuses } from "@hydrologiq/shared/providers/helpers/useCopy"
import { statusToText } from "@hydrologiq/shared/asset/attributes/status"
import { ElectrolyserStatusValue, visualFor } from "./electrolyserStatusVisuals"
import {
  deriveStackStatus,
  flowRateParts,
  stackFlowRate,
  statusUnits,
  type ElectrolyserStack,
} from "./electrolyserStack"

/** How long each flow reading holds before the next slides in. Unhurried on purpose: clicking is
 *  the reliable way to reach a unit, so the timer only has to be a gentle nudge. */
const READING_MS = 6500
const SLIDE_MS = 320

/**
 * Shows one reading at a time, sliding the next in from the right on a timer.
 *
 * Rotating rather than showing both side by side is what keeps this to a single line: together the
 * two readings need ~115px even at 11px, which a 250px card doesn't have spare once a long status
 * like "Available" has taken its share.
 *
 * The animation is driven by a two-phase state rather than keyframes: on each change the new
 * reading is placed offset and transparent with transitions off, then moved to its resting position
 * on the next frame with transitions on.
 *
 * Clicking flips to the next reading and stops the timer for good — once someone has chosen a unit,
 * moving the display off it would undo their choice. There is no way back to rotating short of a
 * remount, which is the intended trade: the timer exists only to reveal that a second unit exists.
 *
 * Under `prefers-reduced-motion` the readings still alternate — otherwise one unit would never be
 * seen — but they cut rather than slide.
 */
function RotatingReading({ readings }: { readings: string[] }) {
  const [index, setIndex] = useState(0)
  const [settled, setSettled] = useState(true)
  const [reduced, setReduced] = useState(false)
  const [hovered, setHovered] = useState(false)
  // Set on first click: choosing a unit is a decision, so the timer stops rather than moving the
  // display off what the user just asked for.
  const [pinned, setPinned] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Paused while the pointer is over it so a value can't flip out from under a click, and stopped
  // for good once the user has picked a unit.
  useEffect(() => {
    if (readings.length < 2 || hovered || pinned) return
    const id = setInterval(() => setIndex((i) => (i + 1) % readings.length), READING_MS)
    return () => clearInterval(id)
  }, [readings.length, hovered, pinned])

  useEffect(() => {
    if (reduced) return
    setSettled(false)
    const raf = requestAnimationFrame(() => setSettled(true))
    return () => cancelAnimationFrame(raf)
  }, [index, reduced])

  const moving = !reduced && !settled
  const next = () => {
    setPinned(true)
    setIndex((i) => (i + 1) % readings.length)
  }

  return (
    // chakra.button rather than Box as="button": it is keyboard reachable and announces itself, and
    // unlike a polymorphic Box it accepts native button attributes such as `type`. The negative
    // margin cancels the padding so the hover background can be wider than the text without
    // shifting it out of line with the status beside it.
    <chakra.button
      type="button"
      aria-label={`Flow rate, ${readings[index]}. Activate to show the next unit.`}
      onClick={next}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      cursor="pointer"
      display="block"
      textAlign="start"
      flexShrink={0}
      minWidth="70px"
      px="5px"
      mx="-5px"
      py="2px"
      mt="1px"
      borderRadius="4px"
      transition="background 140ms"
      _hover={{ bg: "#EEF2F4" }}
      _focusVisible={{ outline: "2px solid #39aed2", outlineOffset: "1px" }}
    >
      {/* Fixed height and hidden overflow so the incoming reading slides within its own frame and
          the card never changes size. */}
      <Box overflow="hidden" height="18px">
        <Text
          fontSize="13px"
          fontWeight="500"
          color="primary_light"
          whiteSpace="nowrap"
          lineHeight="1.35"
          transform={moving ? "translateX(12px)" : "translateX(0)"}
          opacity={moving ? 0 : 1}
          transition={moving ? "none" : `transform ${SLIDE_MS}ms ease-out, opacity ${SLIDE_MS}ms ease-out`}
        >
          {readings[index]}
        </Text>
      </Box>
    </chakra.button>
  )
}

interface ElectrolyserStackFigureProps {
  /** One entry per parent electrolyser that has children (see collectElectrolyserStacks). */
  stacks: ElectrolyserStack[]
  loading?: boolean
  compact?: boolean
}

/**
 * Overview card "Electrolyser Stack" — one summary for a group of related electrolysers, as
 * opposed to ElectrolyserStatusFigure which pages through units individually.
 *
 * Shows, for each stack:
 * - the parent asset's name as the subtitle,
 * - the rolled-up status from `deriveStackStatus` as the bold figure, with the same icon vocabulary
 *   as the per-unit card,
 * - the combined fill rate as small detail beside it, always rendered — children fill too, so this
 *   is the whole stack's contribution and reads 0 rather than disappearing when nothing flows.
 *
 * Multiple stacks page via PaginatedFigureCard, which hides its own controls for a single stack.
 *
 * Both units are kept (see flowRateParts) rather than picking one, but they alternate in place
 * instead of sitting side by side — see RotatingReading for why.
 */
function ElectrolyserStackFigure({
  stacks,
  loading,
  compact = isCompactFigure("summary"),
}: ElectrolyserStackFigureProps) {
  // Same label source as StatusBadge, so a tenant that renames a status sees it renamed here too.
  const statusCopy = useCopyStatuses(TenantStatusCopyType.Asset)

  return (
    <PaginatedFigureCard
      // Pluralised only when there is more than one stack to page through.
      heading={`Electrolyser Stack${stacks.length > 1 ? "s" : ""}`}
      loading={loading}
      compact={compact}
      items={stacks}
      emptyState={
        <FigureContent.Root>
          <FigureContent.SubText>No electrolyser stack</FigureContent.SubText>
          <FigureContent.Value>—</FigureContent.Value>
        </FigureContent.Root>
      }
      // The rolled-up status necessarily hides which unit is in trouble, so the popover lists every
      // unit — that detail is the first thing anyone asks after seeing a stack report "Error".
      renderPopover={(stack) => ({
        title: stack.parentAssetName,
        content: (
          // Figure wraps popover content in a VStack, which centres its children by default —
          // hence the explicit start alignment rather than relying on the popover recipe.
          <VStack width="100%" alignItems="flex-start" textAlign="start" gap={2}>
            {/* No stack name here — the card's subtitle already carries it. */}
            <Text fontSize="12px" color="primary_muted">
              {stack.units.length} unit{stack.units.length === 1 ? "" : "s"} in this stack
            </Text>
            {/* A grid, not a row of nested HStacks: those relied on space-between, which collapses
                when the panel is only as wide as its content, dropping each unit's status and flow
                onto their own lines. Fixed columns keep every unit on one line and line the values
                up with each other, all left-aligned. */}
            <Box
              display="grid"
              gridTemplateColumns="14px minmax(0, 1fr) auto auto"
              alignItems="center"
              columnGap={3}
              rowGap={2}
              width="100%"
              textAlign="start"
            >
              {stack.units.map((unit) => {
                const { icon, color } = visualFor(unit.status)
                return (
                  <Fragment key={unit.id}>
                    <Icon as={icon} color={color} boxSize="14px" aria-hidden />
                    <Text fontSize="13px" color="primary" truncate>
                      {unit.name}
                    </Text>
                    <Text fontSize="13px" color="primary_light" whiteSpace="nowrap">
                      {statusToText(unit.status ?? AssetStatus.Unknown, statusCopy)}
                    </Text>
                    <Text
                      fontSize="13px"
                      color="primary_muted"
                      whiteSpace="nowrap"
                      fontVariantNumeric="tabular-nums"
                    >
                      {unit.flowRate == null ? "— NL/h" : `${Math.round(unit.flowRate)} NL/h`}
                    </Text>
                  </Fragment>
                )
              })}
            </Box>
          </VStack>
        ),
      })}
      /* Card heading names the card, subtitle names the stack, then the status as the bold figure
         with its icon and the combined flow as small detail alongside. Status leads because it is
         what the card exists to answer; the rate qualifies it. */
      renderItem={(stack) => {
        const flow = flowRateParts(stackFlowRate(stack.units))
        return (
          <FigureContent.Root>
            <FigureContent.SubText>{stack.parentAssetName}</FigureContent.SubText>
            <ElectrolyserStatusValue
              status={stack.units[0]?.status ?? deriveStackStatus(statusUnits(stack))}
              statusCopy={statusCopy}
              trailing={<RotatingReading readings={[flow.primary, flow.secondary]} />}
            />
          </FigureContent.Root>
        )
      }}
    />
  )
}

export default ElectrolyserStackFigure
